/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: Sidebar.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Main sidebar component with collapsible functionality, dynamic menu rendering,
    and comprehensive navigation management for the Ring2All Platform.

 🔧 Notes:
    - Implements collapsible sidebar with smooth animations using Framer Motion
    - Supports dynamic menu loading from database with hierarchical structure
    - Integrates with theme store for dynamic width and styling
    - Implements comprehensive navigation state management
    - Supports internationalization with dynamic language switching
    - Includes user profile section and logout functionality
    - Implements responsive design with mobile-friendly interactions

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeStore } from '../../store/theme';
import { useDashboardEditStore } from '../../store/dashboardEdit';
import { useMenuStore } from '../../store/menu';
import { useAuthStore } from '../../store/auth';
import { useI18n } from '../../hooks/useI18n';
import { useBranding } from '../../hooks/useBranding';
import { useSidebarContext } from '../../contexts/SidebarContext';
import { ChevronRight, User } from 'lucide-react';
import { getIconByName } from './sidebarIcons';
import { SidebarUserMenu } from './SidebarUserMenu';
import { AboutModal } from '../AboutModal/AboutModal';
import { api } from '../../lib/api';

export default function Sidebar() {
  // Note: themePrefs is no longer used for sidebar configuration
  const { menuItems, initializeMenu, toggleExpanded, expandedItems, expandForPath, isInitialized } = useMenuStore();
  const { user, logout } = useAuthStore();
  const { t } = useI18n();
  const { brandingData } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, setIsCollapsed } = useSidebarContext();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [avatarKey, setAvatarKey] = useState(0); // Force re-render of avatar
  const [userMenuPos, setUserMenuPos] = useState<{ bottom: number; left: number } | null>(null);
  const avatarContainerRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position from avatar ref
  const toggleUserMenu = useCallback(() => {
    if (showUserMenu) {
      setShowUserMenu(false);
      return;
    }
    if (avatarContainerRef.current) {
      const rect = avatarContainerRef.current.getBoundingClientRect();
      if (isCollapsed) {
        // Flyout to the right of the sidebar (88px + 8px gap)
        // Bottom-aligned with the avatar
        setUserMenuPos({
          bottom: window.innerHeight - rect.bottom,
          left: 96
        });
      } else {
        // Above the avatar, left-aligned with the avatar container
        setUserMenuPos({
          bottom: window.innerHeight - rect.top + 8,
          left: rect.left
        });
      }
    }
    setShowUserMenu(true);
  }, [showUserMenu, isCollapsed]);

  // Note: Sidebar collapsed state is now managed via SidebarContext

  // Force avatar re-render when location changes (fix for Appearance page issue)
  useEffect(() => {
    setAvatarKey(prev => prev + 1);
  }, [location.pathname]);

  const handleSectionClick = (sectionSlug: string) => {
    // If the menu is collapsed, we expand it automatically
    if (isCollapsed) {
      setIsCollapsed(false);
    }

    // Then we handle the section selection
    toggleSection(sectionSlug);
  };

  // Icon mapping function for Level 1 (full size)
  const getIcon = (iconName: string, isActive: boolean = false) => {
    const iconProps = {
      className: `h-7 w-7 ${isActive ? 'text-primary' : 'text-foreground'}`,
      size: 28
    };

    return getIconByName(iconName, iconProps);
  };

  // Icon mapping function for Level 2 and 3 (increased size by 20%)
  const getSmallIcon = (iconName: string, isActive: boolean = false) => {
    const iconProps = {
      className: `h-4 w-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`,
      size: 16
    };

    return getIconByName(iconName, iconProps);
  };

  // getIconByName is now in sidebarIcons.tsx

  // ✅ Initialize hardcoded menu on mount (instant, no API calls)
  useEffect(() => {
    if (!isInitialized && user?.tenant?.slug) {
      initializeMenu(user.tenant.slug);
    }
  }, [isInitialized, user?.tenant?.slug]);

  // Auto-expand menu for current route
  useEffect(() => {
    if (!isInitialized || menuItems.length === 0) return;
    const currentPath = location.pathname;
    if (currentPath === '/' || currentPath === '/dashboard') return;
    expandForPath(currentPath);
  }, [location.pathname, isInitialized]);

  // Sync expandedSections (local state for Level 1) with expandedItems from store
  useEffect(() => {
    // Find which level 1 slugs are expanded
    const level1Slugs = new Set(menuItems.filter(item => item.level === 1).map(item => item.slug!));
    const expandedLevel1 = Array.from(expandedItems).filter(slug => level1Slugs.has(slug));

    // Only allow ONE level 1 section expanded at a time
    if (expandedLevel1.length > 1) {
      const sectionToKeep = expandedLevel1[expandedLevel1.length - 1];
      const updatedExpanded = new Set(expandedItems);
      expandedLevel1.forEach(slug => {
        if (slug !== sectionToKeep) updatedExpanded.delete(slug);
      });
      useMenuStore.setState({ expandedItems: updatedExpanded });
      setExpandedSections(new Set([sectionToKeep]));
    } else {
      setExpandedSections(new Set(expandedLevel1));
    }
  }, [expandedItems, menuItems]);

  const toggleSection = (sectionSlug: string) => {
    const isAlreadyExpanded = expandedSections.has(sectionSlug);
    if (!isAlreadyExpanded) {
      toggleExpanded(sectionSlug, true); // closeOthersAtSameLevel = true
      setExpandedSections(new Set([sectionSlug]));
    }
  };

  const toggleGroup = (groupSlug: string) => {
    toggleExpanded(groupSlug, true); // true = close siblings at same level
  };

  const handleItemClick = (item: { url?: string, external?: boolean, appId?: string }) => {
    if (!item.url) return;

    // ── SSO Apps (Switchboard, Portal, Billing) ───────────────────────────────
    // Detect SSO apps by URL pattern: /api/apps/APPID/redirect
    // We extract the appId from the URL since MenuItem type doesn't persist appId.
    // Pattern: open about:blank immediately (preserves user-gesture → no popup blocker),
    // call the sso-token endpoint via Axios (cookies sent automatically),
    // then navigate the blank tab to the full app URL with the short-lived token.
    const ssoMatch = item.url.match(/^\/api\/apps\/([^/]+)\/redirect/);
    const appId = item.appId || ssoMatch?.[1];

    if (item.external && appId) {
      // Open blank tab synchronously while we still have the click user-gesture
      const newTab = window.open('about:blank', '_blank');
      if (!newTab) {
        // Popup was blocked — fall back to direct URL open
        window.open(item.url, '_blank');
        return;
      }

      // Show a loading spinner in the blank tab
      newTab.document.write(
        '<html><head><title>Opening...</title></head>' +
        '<body style="margin:0;background:#0f172a;color:#94a3b8;font-family:sans-serif;' +
        'display:flex;align-items:center;justify-content:center;height:100vh">' +
        '<div style="text-align:center">' +
        '<div style="width:40px;height:40px;border:3px solid #3b82f6;border-top-color:transparent;' +
        'border-radius:50%;animation:spin .8s linear infinite;margin:0 auto 16px"></div>' +
        '<p style="font-size:1.1rem">Opening application...</p>' +
        '<style>@keyframes spin{to{transform:rotate(360deg)}}</style>' +
        '</div></body></html>'
      );
      newTab.document.close();

      // Request a short-lived SSO token from the Admin API
      api.post(`/api/apps/${appId}/sso-token`, {})
        .then(({ data }) => {
          const token: string = data.token;
          // Each app returns its URL under a different key: switchboardUrl, portalUrl
          const appPath: string =
            data.switchboardUrl ||
            data.portalUrl ||
            `/${appId}`;

          // Convert relative path to absolute URL using the current host
          const base = appPath.startsWith('/')
            ? `${window.location.protocol}//${window.location.host}${appPath}`
            : appPath;

          const finalUrl = `${base.replace(/\/$/, '')}/?sso_token=${encodeURIComponent(token)}`;
          newTab.location.href = finalUrl;
        })
        .catch((err) => {
          console.error(`SSO failed for ${appId}:`, err);
          newTab.document.open();
          newTab.document.write(
            '<html><body style="margin:0;background:#0f172a;color:#ef4444;font-family:sans-serif;' +
            'display:flex;align-items:center;justify-content:center;height:100vh">' +
            '<div style="text-align:center">' +
            '<p style="font-size:1.1rem">Failed to open application. Please try again.</p>' +
            '</div></body></html>'
          );
          newTab.document.close();
        });
      return;
    }

    // ── Standard external link ────────────────────────────────────────────────
    if (item.external) {
      window.open(item.url, '_blank');
      return;
    }

    // ── Internal navigation ───────────────────────────────────────────────────
    navigate(item.url);
    if (isMobile) {
      setIsCollapsed(true);
    }
  };

  const handleGroupClick = (groupSlug: string) => {
    toggleGroup(groupSlug);
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  // Function to get translated title for menu sections
  const getTranslatedTitle = (section: { slug?: string; title?: string }) => {
    // Use slug if available, otherwise generate from title
    const translationKey = section.slug || section.title.toLowerCase().replace(/[^a-z0-9]/g, '_');

    // Use the syntax that works: menu.sections.key
    const translated = t(`menu.sections.${translationKey}`);

    // If translation exists and is different from the key, use it
    if (translated && translated !== `menu.sections.${translationKey}`) {
      return translated;
    }

    // If section.title is already a translation key (contains dots), try to extract the key part
    if (section.title && section.title.includes('.')) {
      const keyFromTitle = section.title.split('.').pop();
      const fallbackTranslation = t(`menu.sections.${keyFromTitle}`);
      if (fallbackTranslation && fallbackTranslation !== `menu.sections.${keyFromTitle}`) {
        return fallbackTranslation;
      }
    }

    // Final fallback: clean title or original title
    return section.title ? section.title.replace(/^sections\./, '').replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Menu Item';
  };

  // Group menu items by level
  const sections = menuItems.filter(item => item.level === 1);
  const groups = menuItems.filter(item => item.level === 2);
  const items = menuItems.filter(item => item.level === 3);

  return (
    <aside
      className="fixed left-0 top-[80px] h-[calc(100vh-80px)] bg-card border-r border-border transition-all duration-300 overflow-visible"
      style={{
        width: isMobile ? '350px' : (isCollapsed ? 'var(--sidebar-collapsed-width, 88px)' : 'var(--sidebar-width, 350px)'),
        transform: isMobile && isCollapsed ? 'translateX(-100%)' : 'none',
        zIndex: 1000
      }}
    >
      <div className="h-full flex flex-col">

        {/* Navigation */}
        <nav className="flex-1 overflow-hidden relative">
          <div className="flex h-full">
            {/* Level 1 - Columna fija de iconos a la izquierda */}
            <div className="w-[88px] p-2 space-y-2 border-r border-border flex flex-col overflow-y-auto overflow-x-visible relative z-[1001] flex-shrink-0">

              {/* Iconos del Nivel 1 */}
              <div className="space-y-2">
                {sections.map((section) => {
                  const isActive = expandedSections.has(section.slug!);
                  return (
                    <motion.button
                      key={section.slug}
                      className={`w-full flex flex-col items-center justify-center rounded-lg transition-all duration-200 py-2 space-y-1 ${isActive
                        ? 'bg-primary/20 border border-primary/30'
                        : 'hover:bg-muted/50'
                        }`}
                      onClick={() => handleSectionClick(section.slug!)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {getIcon(section.icon, isActive)}
                      <div className="w-full px-0.5 min-w-0 flex justify-center">
                        <span
                          className="block w-full text-[10px] text-center text-muted-foreground uppercase leading-tight break-words"
                        >
                          {getTranslatedTitle(section)}
                        </span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Spacer para empujar los botones de abajo */}
              <div className="flex-1"></div>

              {/* User Profile en la parte inferior */}
              <div className="border-t border-border pt-2 relative z-[60]">
                <div className="relative" ref={avatarContainerRef}>
                  <div className="flex justify-center">
                    <motion.div
                      onClick={toggleUserMenu}
                      className="relative cursor-pointer transition-all duration-200 hover:scale-105"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      key={`user-avatar-${avatarKey}`}
                    >
                      {user?.avatar ? (
                        <img
                          src={user.avatar}
                          alt="User Avatar"
                          className="h-12 w-12 object-cover rounded-full border-2 border-border hover:border-primary/50 transition-colors"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              const fallback = parent.querySelector('.avatar-fallback');
                              if (fallback) {
                                (fallback as HTMLElement).style.display = 'flex';
                              }
                            }
                          }}
                        />
                      ) : null}
                      <div
                        className={`h-12 w-12 bg-muted rounded-full flex items-center justify-center border-2 border-border hover:border-primary/50 transition-colors avatar-fallback ${user?.avatar ? 'hidden' : ''}`}
                      >
                        <User className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 h-4 w-4 bg-primary rounded-full border-2 border-card"></div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>

            {/* Columna derecha - Muestra contenido cuando se selecciona un icono del Nivel 1 */}
            <div className={`p-2 transition-all duration-300 relative z-[1000] overflow-y-auto ${isCollapsed ? 'hidden' : 'block'}`} style={{ width: '270px' }}>
              {sections.map((section) => {
                if (!expandedSections.has(section.slug!) || !section.children || section.children.length === 0) {
                  return null;
                }

                return (
                  <div key={section.slug} className="w-full space-y-2 relative z-[1001]">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 relative z-[1002] bg-card">
                      {getTranslatedTitle(section).toUpperCase()}
                    </h3>

                    {/* Level 2 */}
                    {section.children.map((child) => {
                      // Check if current path matches exactly, or if it's a parent of grandchildren
                      // Only mark as active if no grandchildren are more specific
                      // IMPORTANTE: Aplicar el mismo mapeo de rutas que usamos en auto-expand
                      let checkPath = location.pathname;
                      if (location.pathname.includes('/admin/system-settings/system-misc/')) {
                        const pathParts = location.pathname.split('/').filter(Boolean);
                        if (pathParts.length >= 4 && pathParts[0] === 'admin' && pathParts[1] === 'system-settings' && pathParts[2] === 'system-misc') {
                          checkPath = '/admin/system-settings/misc';
                        }
                      }
                      const hasActiveGrandchild = child.children?.some(grandchild => {
                        const grandchildCheckPath = checkPath;
                        return grandchild.url && (grandchildCheckPath === grandchild.url || grandchildCheckPath.startsWith(grandchild.url + '/'));
                      });
                      const isChildActive = !hasActiveGrandchild && (
                        checkPath === child.url ||
                        (child.url && checkPath.startsWith(child.url + '/'))
                      );
                      const isChildExpanded = expandedItems.has(child.slug!);
                      return (
                        <div key={child.slug} className="w-full space-y-1 relative z-[1000]">
                          <motion.button
                            onClick={(e) => {
                              if (child.hasChildren || (child.children && child.children.length > 0)) {
                                handleGroupClick(child.slug!);
                              } else {
                                handleItemClick(child);
                              }
                            }}
                            className={`w-full flex items-center justify-between px-2 py-2 rounded-lg transition-all duration-200 ${isChildActive
                              ? 'bg-primary/20 border border-primary/30'
                              : 'hover:bg-muted/50'
                              }`}
                            whileHover={{ x: 4 }}
                            whileTap={{ scale: 0.98 }}
                            style={{ width: '100%' }}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              {getSmallIcon(child.icon, isChildActive)}
                              <span className={`text-sm font-medium text-left flex-1 ${isChildActive ? 'text-primary' : 'text-foreground/80'}`}>
                                {getTranslatedTitle(child)}
                              </span>
                            </div>
                            {(child.hasChildren || (child.children && child.children.length > 0)) && (
                              <motion.div
                                animate={{ rotate: isChildExpanded ? 90 : 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              </motion.div>
                            )}
                          </motion.button>

                          {/* Level 3 */}
                          <AnimatePresence>
                            {isChildExpanded && (
                              <motion.div
                                className="ml-6 space-y-1 relative z-[999]"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                {child.children && child.children.length > 0 && child.children.map((grandchild) => {
                                  let grandchildCheckPath = location.pathname;
                                  if (location.pathname.includes('/admin/system-settings/system-misc/')) {
                                    const pathParts = location.pathname.split('/').filter(Boolean);
                                    if (pathParts.length >= 4 && pathParts[0] === 'admin' && pathParts[1] === 'system-settings' && pathParts[2] === 'system-misc') {
                                      grandchildCheckPath = '/admin/system-settings/misc';
                                    }
                                  }
                                  const isGrandchildActive = grandchildCheckPath === grandchild.url || (grandchild.url && grandchildCheckPath.startsWith(grandchild.url + '/'));
                                  return (
                                    <motion.button
                                      key={grandchild.slug}
                                      onClick={() => handleItemClick(grandchild)}
                                      className={`w-full flex items-center justify-between px-2 py-2 rounded-lg transition-all duration-200 ${isGrandchildActive
                                        ? 'bg-primary/20 border border-primary/30'
                                        : 'hover:bg-muted/50'
                                        }`}
                                      whileHover={{ x: 4 }}
                                      whileTap={{ scale: 0.98 }}
                                      style={{ width: '100%' }}
                                    >
                                      <div className="flex items-center gap-3 flex-1">
                                        {getSmallIcon(grandchild.icon, isGrandchildActive)}
                                        <span className={`text-sm text-left flex-1 ${isGrandchildActive ? 'text-primary' : 'text-foreground/70'}`}>
                                          {getTranslatedTitle(grandchild)}
                                        </span>
                                      </div>
                                    </motion.button>
                                  );
                                })}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </nav>

      </div>

      <SidebarUserMenu
        show={showUserMenu}
        position={userMenuPos}
        user={user}
        onNavigate={(path) => { navigate(path); setShowUserMenu(false); }}
        onLogout={handleLogout}
        onOpenAbout={() => { setShowAboutModal(true); setShowUserMenu(false); }}
        t={t}
      />

      <AboutModal
        isOpen={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />

      {/* Overlay for flyouts */}
      {(hoveredSection && isCollapsed) || showUserMenu ? (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setHoveredSection(null);
            setShowUserMenu(false);
          }}
        />
      ) : null}
    </aside>
  );
}
